import config from '../config';

const COGNITO_ENDPOINT = `https://cognito-idp.${config.cognito.region}.amazonaws.com/`;

// Read lazily at call time so we always get the current config value, and throw a
// developer-readable error rather than silently passing '' to every Cognito request.
function getClientId(): string {
  const id = config.cognito.appClientId;
  if (!id) {
    throw new Error(
      'Cognito App Client ID is not configured (EXPO_PUBLIC_APP_CLIENT_ID is empty). ' +
      'Run `npm run env:development` to generate .env.local, then rebuild the app. ' +
      'For EAS builds, ensure the secret is set via `eas secret:create`.'
    );
  }
  return id;
}

export interface NewDeviceMetadata {
  DeviceKey: string;
  DeviceGroupKey: string;
}

interface CognitoResponse {
  AuthenticationResult?: {
    IdToken: string;
    AccessToken: string;
    RefreshToken: string;
    ExpiresIn: number;
    // Present whenever the user pool has DeviceConfiguration set — which ours
    // does. Cognito creates an UNCONFIRMED device entry alongside it, and an
    // unconfirmed device's refresh token is rejected by REFRESH_TOKEN_AUTH.
    // See cognitoDeviceSrp.ts and confirmAndRememberDevice below.
    NewDeviceMetadata?: NewDeviceMetadata;
  };
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
}

// Wrap fetch so a network-level failure (offline, DNS, CORS, Safari's
// "TypeError: Load failed") surfaces as a typed, user-readable error instead of
// a cryptic one. Without this, a transient network blip during sign-in shows up
// verbatim under the password field as "Load failed" — which reads to the user
// as "wrong credentials" — and the same raw rejection escapes elsewhere as an
// unhandled rejection.
// A stalled connection is not the same as a failed one, and only the second
// used to be survivable. `fetch` rejects when the network is plainly down
// (offline, DNS, CORS) — that lands in the catch below and surfaces as a
// typed, retryable error. But a socket that opens and then goes quiet never
// rejects AND never resolves: wifi still associating after a laptop wakes, a
// VPN reconnecting, a captive portal swallowing the request. Without a
// deadline that await hangs forever, and every caller waiting on it hangs too.
//
// That is not hypothetical. The desktop lock screen calls this through
// getFreshTokenOrSignal on resume from sleep — the first thing to run, at the
// exact moment the network is least likely to be up — and a hang there left
// the unlock button spinning and disabled, with no error and no way back to
// the PIN pad short of signing out.
//
// 20s is well beyond a healthy InitiateAuth (tens of milliseconds) and well
// inside a person's patience. On timeout we throw the SAME typed error a
// hard network failure produces, so every existing caller's handling applies
// unchanged — the fix adds a deadline, it does not add a new failure mode.
const COGNITO_TIMEOUT_MS = 20_000;

async function cognitoFetch(target: string, body: object): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), COGNITO_TIMEOUT_MS);
  try {
    return await fetch(COGNITO_ENDPOINT, {
      method: 'POST',
      headers: {
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
        'Content-Type': 'application/x-amz-json-1.1',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch {
    const error: any = new Error('Network error — please check your connection and try again.');
    error.code = 'NetworkError';
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

async function cognitoRequest(target: string, body: object): Promise<CognitoResponse> {
  const res = await cognitoFetch(target, body);

  const data = await res.json();

  if (!res.ok) {
    const message = data.message || data.Message || data.__type || 'Authentication failed.';
    const error: any = new Error(message);
    error.code = data.__type || 'UnknownError';
    throw error;
  }

  return data;
}

export async function cognitoSignIn(username: string, password: string): Promise<CognitoResponse> {
  return cognitoRequest('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    AuthParameters: {
      USERNAME: username.trim().toLowerCase(),
      PASSWORD: password,
    },
    ClientId: getClientId(),
  });
}

export async function cognitoRespondToChallenge(
  challengeName: string,
  session: string,
  username: string,
  response: string
): Promise<CognitoResponse> {
  const challengeResponses: Record<string, string> = { USERNAME: username };

  if (challengeName === 'SMS_MFA' || challengeName === 'SOFTWARE_TOKEN_MFA') {
    challengeResponses[challengeName === 'SMS_MFA' ? 'SMS_MFA_CODE' : 'SOFTWARE_TOKEN_MFA_CODE'] = response;
  } else if (challengeName === 'NEW_PASSWORD_REQUIRED') {
    challengeResponses['NEW_PASSWORD'] = response;
  } else {
    challengeResponses['answer'] = response;
  }

  return cognitoRequest('RespondToAuthChallenge', {
    ChallengeName: challengeName,
    ChallengeResponses: challengeResponses,
    Session: session,
    ClientId: getClientId(),
  });
}

export async function cognitoRefreshSession(
  refreshToken: string,
  clientId?: string,
  deviceKey?: string,
): Promise<CognitoResponse> {
  const authParameters: Record<string, string> = { REFRESH_TOKEN: refreshToken };
  if (deviceKey) authParameters.DEVICE_KEY = deviceKey;
  return cognitoRequest('InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    AuthParameters: authParameters,
    ClientId: clientId || getClientId(),
  });
}

/**
 * Confirm + remember this device so Cognito will accept REFRESH_TOKEN_AUTH.
 *
 * Port of the backend's confirmAndRememberDevice (oauth/authorize), which fixed
 * the identical symptom on the Cadence Mac client: "every press of Refresh logs
 * me out." Mobile hits it as a hard sign-out roughly once every 24 hours — the
 * refresh is only attempted after the ID token expires, and a rejection there
 * is a definitive NotAuthorizedException, so handleAuthExpiry clears the tokens
 * and the app lands on the login screen.
 *
 * Two calls, both required:
 *   1. ConfirmDevice binds an SRP password verifier to the device.
 *   2. UpdateDeviceStatus flips it from `not_remembered` — which is what
 *      ConfirmDevice leaves it at while the pool sets
 *      DeviceOnlyRememberedOnUserPrompt — to `remembered`. Cognito can also
 *      require DEVICE_KEY during REFRESH_TOKEN_AUTH, so callers persist and
 *      supply it with the refresh token.
 *
 * Throws on failure. The caller decides what that means; it must never fail a
 * sign-in, because the cost of failing here is a logout tomorrow, not now.
 */
export async function cognitoConfirmDevice(
  accessToken: string,
  deviceKey: string,
  deviceSecretVerifierConfig: { PasswordVerifier: string; Salt: string },
  deviceName = 'Eleplan mobile',
): Promise<void> {
  await cognitoRequest('ConfirmDevice', {
    AccessToken: accessToken,
    DeviceKey: deviceKey,
    DeviceName: deviceName,
    DeviceSecretVerifierConfig: deviceSecretVerifierConfig,
  });
}

export async function cognitoUpdateDeviceStatus(
  accessToken: string,
  deviceKey: string,
  status: 'remembered' | 'not_remembered' = 'remembered',
): Promise<void> {
  await cognitoRequest('UpdateDeviceStatus', {
    AccessToken: accessToken,
    DeviceKey: deviceKey,
    DeviceRememberedStatus: status,
  });
}

export async function cognitoForgotPassword(username: string): Promise<void> {
  await cognitoRequest('ForgotPassword', {
    Username: username.trim().toLowerCase(),
    ClientId: getClientId(),
  });
}

export async function cognitoConfirmForgotPassword(
  username: string,
  code: string,
  newPassword: string
): Promise<void> {
  await cognitoRequest('ConfirmForgotPassword', {
    Username: username.trim().toLowerCase(),
    ConfirmationCode: code,
    Password: newPassword,
    ClientId: getClientId(),
  });
}

export async function cognitoSignUp(
  username: string,
  password: string,
  attributes: { email: string; name: string; given_name: string; family_name: string; phone_number?: string }
): Promise<{ UserSub: string }> {
  const userAttributes = [
    { Name: 'email', Value: attributes.email },
    { Name: 'name', Value: `${attributes.given_name} ${attributes.family_name}`.trim() },
    { Name: 'given_name', Value: attributes.given_name },
    { Name: 'family_name', Value: attributes.family_name },
  ];
  if (attributes.phone_number) {
    userAttributes.push({ Name: 'phone_number', Value: attributes.phone_number });
  }

  const data = await cognitoRequest('SignUp', {
    ClientId: getClientId(),
    Username: username.trim().toLowerCase(),
    Password: password,
    UserAttributes: userAttributes,
  }) as any;

  return { UserSub: data.UserSub };
}

export async function cognitoConfirmSignUp(
  username: string,
  code: string
): Promise<void> {
  await cognitoRequest('ConfirmSignUp', {
    ClientId: getClientId(),
    Username: username.trim().toLowerCase(),
    ConfirmationCode: code,
  });
}

export async function cognitoResendConfirmationCode(
  username: string
): Promise<void> {
  await cognitoRequest('ResendConfirmationCode', {
    ClientId: getClientId(),
    Username: username.trim().toLowerCase(),
  });
}

// ─── MFA management (require AccessToken, not client credentials) ─────────────

async function cognitoUserRequest(target: string, body: object): Promise<any> {
  const res = await cognitoFetch(target, body);
  const data = await res.json();
  if (!res.ok) {
    const message = data.message || data.Message || data.__type || 'Request failed.';
    const error: any = new Error(message);
    error.code = data.__type || 'UnknownError';
    throw error;
  }
  return data;
}

/** Start TOTP setup — returns the secret key to encode into a QR code */
export async function cognitoChangePassword(accessToken: string, previousPassword: string, proposedPassword: string): Promise<void> {
  await cognitoRequest('ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
  // cognitoRequest throws on non-2xx, so reaching here means success
}

export async function cognitoAssociateSoftwareToken(accessToken: string): Promise<string> {
  const data = await cognitoUserRequest('AssociateSoftwareToken', { AccessToken: accessToken });
  return data.SecretCode as string;
}

/** Verify the 6-digit TOTP code from the authenticator app */
export async function cognitoVerifySoftwareToken(
  accessToken: string,
  userCode: string,
  deviceName = 'Authenticator'
): Promise<void> {
  await cognitoUserRequest('VerifySoftwareToken', {
    AccessToken: accessToken,
    UserCode: userCode,
    FriendlyDeviceName: deviceName,
  });
}

/** Set the preferred MFA method for the signed-in user */
export async function cognitoSetMFAPreference(
  accessToken: string,
  type: 'TOTP' | 'SMS' | 'NOMFA'
): Promise<void> {
  const smsSetting = { Enabled: type === 'SMS', PreferredMfa: type === 'SMS' };
  const totpSetting = { Enabled: type === 'TOTP', PreferredMfa: type === 'TOTP' };
  await cognitoUserRequest('SetUserMFAPreference', {
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: totpSetting,
    SMSMfaSettings: smsSetting,
  });
}

/** Get current user attributes + MFA preference */
export async function cognitoGetUser(accessToken: string): Promise<{
  preferredMFA: string | null;
  phoneVerified: boolean;
  phone: string | null;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  name: string | null;
}> {
  const data = await cognitoUserRequest('GetUser', { AccessToken: accessToken });
  const attrs: { Name: string; Value: string }[] = data.UserAttributes || [];
  const attrMap = Object.fromEntries(attrs.map((a) => [a.Name, a.Value]));
  const mfaList: { DeliveryMedium?: string; AttributeName?: string }[] = data.MFAOptions || [];
  let preferredMFA: string | null = null;
  if (data.PreferredMfaSetting) preferredMFA = data.PreferredMfaSetting;
  else if (mfaList.length) preferredMFA = mfaList[0].DeliveryMedium === 'SMS' ? 'SMS_MFA' : 'SOFTWARE_TOKEN_MFA';
  return {
    preferredMFA,
    phoneVerified: attrMap['phone_number_verified'] === 'true',
    phone: attrMap['phone_number'] || null,
    email: attrMap['email'] || null,
    givenName: attrMap['given_name'] || null,
    familyName: attrMap['family_name'] || null,
    name: attrMap['name'] || null,
  };
}

/** Send a verification code to the user's phone number attribute */
export async function cognitoGetAttributeVerificationCode(
  accessToken: string,
  attributeName: 'phone_number' | 'email' = 'phone_number'
): Promise<void> {
  await cognitoUserRequest('GetUserAttributeVerificationCode', {
    AccessToken: accessToken,
    AttributeName: attributeName,
  });
}

/** Submit the verification code for a user attribute */
export async function cognitoVerifyUserAttribute(
  accessToken: string,
  attributeName: 'phone_number' | 'email',
  code: string
): Promise<void> {
  await cognitoUserRequest('VerifyUserAttribute', {
    AccessToken: accessToken,
    AttributeName: attributeName,
    Code: code,
  });
}
